import { type FC, memo, type ReactNode } from "react";

import { IconType } from "~/common/components/icon/IconType";
import { Link } from "~/common/components/Link";
import { Icon } from "~/common/components/icon/Icon";
import { URL_BULK_TEMPLATE, URL_BULK_TEMPLATE_XLSX } from "~/utils/url/staticUrls";
import { userCanSeePriceReference } from "~/features/cart/cart/components/LineItemStaticParams";
import { DateFormatType, getPattern } from "~/common/utils/DateFormatter";
import { useTranslation } from "react-i18next";
import { Accordion, AccordionDetails, AccordionSummary, Box, Stack, Typography } from "~/common/mui";

const DownloadLink = ({ href, children }: { href: string; children: ReactNode }) => (
  <Link href={href} iconBefore={<Icon type={IconType.Download} />} skipResolve={true}>
    {children}
  </Link>
);

const HelpSection: FC = () => {
  const { t } = useTranslation();

  return (
    <Box sx={{ mt: 1, mb: 3 }}>
      <Typography sx={{ mb: 1 }}>{t("bulkUpload.help.headline")}</Typography>
      <Accordion>
        <AccordionSummary>{t("bulkUpload.help.upload")}</AccordionSummary>
        <AccordionDetails>
          <Stack gap={2}>
            <Typography>{t("bulkUpload.help.item1.paragraph1")}</Typography>

            <Box>
              <Typography variant="titleSmall" color="textSecondary">
                {t("bulkUpload.help.item1.header1")}
              </Typography>
              <Typography>
                {t("bulkUpload.help.item1.paragraph2", {
                  bulkMaxRows: window.SCHA.config.bulkMaxRows,
                })}
              </Typography>
              <DownloadLink href={URL_BULK_TEMPLATE_XLSX}>{t("bulkUpload.help.item1.downloadButton1")}</DownloadLink>
            </Box>
            <Box>
              <Typography>
                {t("bulkUpload.help.item1.paragraph3", {
                  dateFormat: getPattern(DateFormatType.DATE),
                })}
              </Typography>
              <DownloadLink href={URL_BULK_TEMPLATE}>{t("bulkUpload.help.item1.downloadButton2")}</DownloadLink>
            </Box>
            <Typography>{t("bulkUpload.help.item1.paragraph4")}</Typography>
          </Stack>
        </AccordionDetails>
      </Accordion>
      <Accordion>
        <AccordionSummary>{t("bulkUpload.help.text")}</AccordionSummary>
        <AccordionDetails>
          <Stack gap={2}>
            <table className={"scha__bulk-add__help-container__example-table"}>
              <tbody>
                <tr className={"title-medium"}>
                  <td>0621631590000</td>
                  <td>,</td>
                  <td>100</td>
                  <td>,</td>
                  <td>{getPattern(DateFormatType.DATE)}</td>
                  <td>,</td>
                  <td>{t("bulkUpload.help.example.comment")}</td>
                  {userCanSeePriceReference() && <td>{t("bulkUpload.help.example.priceReference")}</td>}
                </tr>

                <tr className={"label-large"}>
                  <td>{t("bulkUpload.help.example.explanation.designation")}</td>
                  <td />
                  <td>{t("bulkUpload.help.example.explanation.quantity")}</td>
                  <td />
                  <td>{t("bulkUpload.help.example.explanation.rdd")}</td>
                  <td />
                  <td>{t("bulkUpload.help.example.explanation.comment")}</td>
                  {userCanSeePriceReference() && <td>{t("bulkUpload.help.example.explanation.priceReference")}</td>}
                </tr>
              </tbody>
            </table>

            <Typography>
              {t("bulkUpload.help.item2.paragraph1", {
                bulkMaxRows: window.SCHA.config.bulkMaxRows,
                dateFormat: getPattern(DateFormatType.DATE),
              })}
            </Typography>

            <Typography>{t("bulkUpload.help.item2.paragraph2")}</Typography>
          </Stack>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default memo(HelpSection);
